import { useEffect, useState } from "react";
import { AlertOctagon, X, Code2, FileWarning } from "lucide-react";
import { compileErrorBus, type CapturedError } from "@/lib/compileErrorBus";

/**
 * Dev-only floating banner that surfaces compile (Vite HMR) and runtime errors,
 * highlighting the missing identifier (e.g. "WhatsBrokenBanner") with file:line.
 */
export default function DevCompileErrorOverlay() {
  const [errors, setErrors] = useState<CapturedError[]>([]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    compileErrorBus.install();
    return compileErrorBus.subscribe(setErrors);
  }, []);

  if (!import.meta.env.DEV || errors.length === 0) return null;

  const top = errors[0];

  return (
    <div
      role="alert"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[2147483647] w-[min(960px,calc(100vw-1.5rem))] pointer-events-auto"
    >
      <div className="rounded-xl border border-rose-500/60 bg-rose-950/95 text-rose-50 shadow-2xl backdrop-blur-md overflow-hidden font-mono">
        <div className="flex items-start gap-3 px-4 py-3 border-b border-rose-500/40">
          <AlertOctagon className="h-5 w-5 mt-0.5 shrink-0 text-rose-300" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-[0.18em] text-rose-300/80">
              <span>
                {top.kind === "compile" ? "Compile error" : "Runtime error"}
              </span>
              {errors.length > 1 && (
                <span className="text-rose-200/70">+{errors.length - 1} meer</span>
              )}
            </div>
            {top.missingSymbol && (
              <p className="mt-1 text-base text-rose-50">
                Ontbrekend symbool:{" "}
                <code className="px-1.5 py-0.5 rounded bg-rose-500/30 text-rose-50 font-semibold">
                  {top.missingSymbol}
                </code>
              </p>
            )}
            <p className="mt-1 text-sm text-rose-100/90 break-words">
              {top.message}
            </p>
            {(top.file || top.line) && (
              <p className="mt-1.5 text-xs text-rose-200/80 break-all flex items-center gap-1.5">
                <FileWarning className="h-3.5 w-3.5" />
                <span>
                  {top.file ?? "onbekend bestand"}
                  {top.line ? `:${top.line}` : ""}
                  {top.column ? `:${top.column}` : ""}
                </span>
              </p>
            )}
            {top.frame && (
              <pre className="mt-2 text-[11px] leading-snug text-rose-100/80 bg-rose-500/10 border border-rose-500/30 rounded p-2 overflow-auto max-h-40 whitespace-pre">
                {top.frame}
              </pre>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => compileErrorBus.dismiss(top.id)}
              className="rounded-md px-2 py-1 text-[11px] uppercase tracking-wider hover:bg-rose-500/20 transition"
              aria-label="Verberg deze fout"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {errors.length > 1 && (
          <details className="px-4 py-2 text-xs">
            <summary className="cursor-pointer text-rose-200/80 hover:text-rose-100 inline-flex items-center gap-1.5">
              <Code2 className="h-3 w-3" />
              Toon overige {errors.length - 1} fout(en)
            </summary>
            <ul className="mt-2 space-y-1.5 pl-1">
              {errors.slice(1).map((e) => (
                <li key={e.id} className="border-l-2 border-rose-500/40 pl-2">
                  {e.missingSymbol && (
                    <code className="text-rose-100 font-semibold">
                      {e.missingSymbol}
                    </code>
                  )}{" "}
                  <span className="text-rose-100/80">{e.message}</span>
                  {e.file && (
                    <div className="text-rose-200/70 break-all">
                      {e.file}
                      {e.line ? `:${e.line}` : ""}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}
